import { atom, noWait } from "recoil";
import {
  selectorFamilyWithoutCache as selectorFamily,
  selectorWithoutCache as selector,
} from "util/RecoilWithoutCache";
import { BusinessLineEnum } from "constants/base";
import type { BusinessLinePositionLevelList } from "api/ct";
import {
  contractList,
  fetchPositionLevelList,
  TopLevelContractList,
} from "api/ct";
import { getOpenSymbol } from "api/trigger";
import { CurrentBusinessLineState } from "./Base";
import {
  MixContractInfo,
  MixContractPlaceInfo,
  PositionLeverageLevel,
} from "store/entity";
import { isSuccess } from "util/Common";
import { KeyOfOptionAll, USDT_ASSET_MODE_TYPE } from "constants/vars";
import { keyBy, mapValues } from "lodash-es";
import { isBrowserEnv } from "@app/utils/constants";
import { queryClient } from "@app/client/queryClient";
import { futuresPlaceConfig } from "api/ssr/ticker";
import { captureException } from "@sentry/react";

export function createDefaultMixContractPlaceInfo(): MixContractPlaceInfo {
  return {
    baseCoinPlace: 2,
    capitalRatePlace: 4,
    delegatePricePlace: 2,
    longLimitBurstPrice: 0.0001,
    marginRatePlace: 2,
    priceEndStep: 1,
    pricePlace: 2,
    propertyHighPlace: 8,
    propertyPlace: 4,
    serverCalPlace: 12,
    shortLimitBurstPrice: 99999999,
    transferInOutPlace: 4,
    volumePlace: 4,
    minDelegateCount: "0.0001",
    countMultiplier: "0.0001",
  };
}

/**
 * 生成单个默认档位
 */
function createDefaultPositionLevel(): PositionLeverageLevel {
  return {
    startUnit: 0,
    endUnit: 10000,
    keepMarginRate: "0.005",
    level: 1,
    leverage: 100,
  };
}

export function createDefaultMixContractInfo(
  symbolId?: string,
  symbolCode?: string
) {
  return {
    baseSymbol: "",
    baseSymbolId: 0,
    businessLine: 3,
    businessType: 3,
    feeRate: 0.0006,
    feeRateUpRatio: 0.01,
    openCostUpRatio: 0.01,
    placeConfig: createDefaultMixContractPlaceInfo(),
    contractLevelList: [
      {
        startUnit: 0,
        endUnit: 10000,
        keepMarginRate: "0.005",
        level: 1,
        leverage: 100,
      },
    ],
    pricedSymbol: "",
    symbolCode: symbolCode || "",
    symbolDisplayName: "",
    symbolCodeDisplayName: "",
    symbolId: symbolId || "BTCUSDT_UMCBL",
    buyLimitPriceRatio: 0.1,
    sellLimitPriceRatio: 0.1,
    supportMarginTokenIds: [],
    indexDataSource: "",
    betaShow: false,
    takerFeeRate: "",
    symbolType: 0,
    maxLeverage: 125,
  } as MixContractInfo;
}

/**
 * 全局：按照业务线与对应合约列表分离开的全体数据
 */
export const AllContractListSelector = selector<TopLevelContractList>({
  key: "top-level-ct-list",
  get: async () => {
    return [];
    try {
      if (isBrowserEnv) {
        const res = await contractList();
        if (isSuccess(res?.code)) {
          return res?.data || [];
        }
        return [];
      }
      return [];
    } catch (e) {
      console.error("get ct list error:", e);
      return [];
    }
  },
});

export const ContractSymbolMapSelector = selector({
  key: "top-level-ct-symbol-map",
  get: ({ get }) => {
    const list = get(AllContractListSelector);
    const businessLineMap = keyBy(list, "businessLine");
    return mapValues(businessLineMap, (item) =>
      keyBy(item.symbolDTOList, "symbolId")
    );
  },
});

interface ContractAllMapAndListSelectorType {
  contractMap: {
    [key: number]: MixContractInfo[];
  };
  contractList: MixContractInfo[];
}

const defaultContractAllMapAndList = {
  contractMap: {},
  contractList: [],
};

/**
 * 合约 所有业务线对应的合约列表
 */
export const ContractAllMapAndListSelector = selector({
  key: "all-ct-map-selector",
  get: ({ get }) => {
    try {
      const base = get(AllContractListSelector);
      // const setContractAllList = useSetRecoilState(ContractAllListState)
      // const setContractAllMap = useSetRecoilState(ContractAllMapState)
      if (!base) return defaultContractAllMapAndList;
      const contractMap = base?.reduce((map, item) => {
        map[item.businessLine] = item.symbolDTOList;
        return map;
      }, {});
      const contractList = base?.reduce((list, item) => {
        list.push(...item.symbolDTOList);
        return list;
      }, []);
      // setContractAllMap(contractMap)
      // setContractAllList(contractList)
      return {
        contractMap,
        contractList,
      } as ContractAllMapAndListSelectorType;
    } catch (error) {
      console.log("error: ", error);
      return defaultContractAllMapAndList;
    }
  },
});

/**
 * 合约 所有业务线对应的合约列表 大佬写的代码 慎用哈 目前只有计算器用了
 */
export const ContractAllListState = atom({
  key: "all-ct-list",
  default: [],
});

/**
 * 合约 所有业务线对应的合约Map
 */
export const ContractAllMapState = atom({
  key: "all-ct-map",
  default: {},
});

/**
 * 根据具体业务线查找对应的合约列表
 */
export const ContractListByBusinessLineSelector = selectorFamily({
  key: "second-level-ct-list",
  get:
    (businessLine: BusinessLineEnum) =>
    ({ get }) => {
      return [];
      const topLevelContractList = get(AllContractListSelector);
      const find = topLevelContractList.find(
        (item) => item.businessLine === businessLine
      );

      if (find) {
        return find.symbolDTOList.filter((i) => {
          // 线上要拼比掉betaShow为true的交易对
          if (process.env.REACT_APP_ENV === "online") {
            return !i.betaShow;
          } else {
            return true;
          }
        });
      } else {
        return [];
      }
    },
});

/**
 * 当前业务线的合约币对列表
 * @deprecated use `CurrentContractListSyncSelector` or `CurrentContractListSelector` instead
 */
export const CurrentBusinessLineContractListState = atom<MixContractInfo[]>({
  key: "current-business-line-ct-list",
  default: [],
});

/**
 * 当前业务线所属的合约列表
 */
export const CurrentContractListSelector = selector<MixContractInfo[]>({
  key: "current-level-ct-list",
  get: ({ get }) => {
    const currentBusinessLine = get(CurrentBusinessLineState);
    return get(ContractListByBusinessLineSelector(currentBusinessLine));
  },
});

export const CurrentContractListSyncSelector = selector<MixContractInfo[]>({
  key: "current-level-ct-list-sync",
  get: ({ get }) => {
    const loadable = get(noWait(CurrentContractListSelector));
    if (loadable.state === "hasValue") {
      return loadable.contents;
    } else {
      return [];
    }
  },
});

/**
 * 当前交易对配置信息 包含 精度等
 */
export const MixContractInfoState = atom({
  key: "mix-ct-info-state",
  default: createDefaultMixContractInfo(),
});

export const CurrentAppPathnameState = atom({
  key: "current-app-pathname-state",
  default: "",
});

/**
 * 当前交易对配置信息 包含 精度等 - selector 在币对价格详情中SSR使用，其他地方禁用
 */
export const MixContractInfoSelector = selector<MixContractInfo>({
  key: "mix-ct-info-selector",
  get: ({ get }) => {
    const serverCachePlaceConfig =
      queryClient.getQueryData<MixContractPlaceInfo>([
        ...futuresPlaceConfig.queryKey,
        get(CurrentAppPathnameState),
      ]);
    const baseMixContractInfoState = get(MixContractInfoState);
    return {
      ...baseMixContractInfoState,
      placeConfig:
        serverCachePlaceConfig || baseMixContractInfoState.placeConfig,
    };
  },
});

export const MixContractInfoMapSelector = selector({
  key: "mix-ct-info-map-selector",
  get: ({ get }) => {
    const base = get(CurrentBusinessLineContractListState);
    // 防止获取失败做的一个小动作
    const map = new Map<string, MixContractInfo>();

    let _get = map.get;

    map.get = function (key: string) {
      return _get.call(this, key) || createDefaultMixContractInfo();
    };

    return base.reduce((map, info) => {
      map.set(info.symbolId, info);
      return map;
    }, map);
  },
});

/**
 * 按照合约symbolId查询对应的配置信息-当前业务线
 */
export const getMixContractInfoBySymbolId = selectorFamily({
  key: "get-mix-ct-info-selector",
  get:
    (symbolId: string) =>
    ({ get }) => {
      const base = get(CurrentBusinessLineContractListState);
      return (
        base.find((config) => config.symbolId === symbolId) ||
        createDefaultMixContractInfo()
      );
    },
});

/**
 * 按照合约symbolId查询对应的配置信息-所有业务线
 */
export const getMixContractInfoBySymbolIdAll = selectorFamily({
  key: "get-mix-ct-info-selector-all",
  get:
    (symbolId: string) =>
    ({ get }) => {
      const base = get(ContractAllListState);
      return (
        base.find((config) => config.symbolId === symbolId) ||
        createDefaultMixContractInfo()
      );
    },
});

type PositionLevelList = BusinessLinePositionLevelList["symbolLevels"];

/**
 * 当前业务线的合约档位列表
 */
export const CurrentBusinessLineContractPositionLevelListState =
  selector<PositionLevelList>({
    key: "current-business-line-ct-position-level-list",
    get: async ({ get }): Promise<PositionLevelList> => {
      return [] as unknown as PositionLevelList;
      const businessLine = get(CurrentBusinessLineState);
      try {
        if (isBrowserEnv) {
          const res = await fetchPositionLevelList(businessLine);
          if (isSuccess(res?.code)) {
            return (
              res?.data?.[0]?.symbolLevels ||
              ([] as unknown as PositionLevelList)
            );
          } else {
            return [] as unknown as PositionLevelList;
          }
        }
        return [] as unknown as PositionLevelList;
      } catch (e) {
        console.error("get ct list error:", e);
        return [] as unknown as PositionLevelList;
      }
    },
  });

/**
 * 指定业务线的合约档位列表
 * */
export const GetContractPositionLevelListByBusinessLine = selectorFamily({
  key: "get-ct-position-level-list-by-business-line",
  get:
    (businessLine: BusinessLineEnum) =>
    async ({ get }): Promise<PositionLevelList> => {
      try {
        if (isBrowserEnv) {
          const res = await fetchPositionLevelList(businessLine);
          if (isSuccess(res?.code)) {
            return (
              res?.data?.[0]?.symbolLevels ||
              ([] as unknown as PositionLevelList)
            );
          } else {
            return [] as unknown as PositionLevelList;
          }
        }
        return [] as unknown as PositionLevelList;
      } catch (e) {
        console.error("get ct list error:", e);
        return [] as unknown as PositionLevelList;
      }
    },
});

/**
 * 指定业务线的合约档位列表(计算器)
 * */
export const GetContractPositionLevelListByBusinessLineCal = selector({
  key: "get-ct-position-level-list-by-business-line-cal",
  get: ({ get }): Promise<PositionLevelList> => {
    return async ({ businessLine }): number => {
      try {
        if (isBrowserEnv) {
          const res = await fetchPositionLevelList(businessLine);
          if (isSuccess(res?.code)) {
            return (
              res?.data?.[0]?.symbolLevels ||
              ([] as unknown as PositionLevelList)
            );
          } else {
            return [] as unknown as PositionLevelList;
          }
        }
        return [] as unknown as PositionLevelList;
      } catch (e) {
        console.error("get ct list error:", e);
        return [] as unknown as PositionLevelList;
      }
      return [] as unknown as PositionLevelList;
    };
  },
});

/**
 * 全局业务线的合约档位列表
 */
export const GlobalContractPositionLevelListState = selector<PositionLevelList>(
  {
    key: "global-ct-position-level-list-state",
    get: async ({ get }): Promise<PositionLevelList> => {
      try {
        if (isBrowserEnv) {
          const res = await fetchPositionLevelList();
          if (isSuccess(res?.code)) {
            // 需要合并data数组中的symbolLevels数据
            const mergedData = [] as unknown as PositionLevelList;
            res.data.forEach((item) => {
              mergedData.push(...item.symbolLevels);
            });
            return mergedData || ([] as unknown as PositionLevelList);
          } else {
            return [] as unknown as PositionLevelList;
          }
        }
        return [] as unknown as PositionLevelList;
      } catch (e) {
        console.error("get ct list error:", e);
        captureException(e);
        return [] as unknown as PositionLevelList;
      }
    },
  }
);

/**
 * 获取传入的symbolId的仓位档位，从当前业务线的仓位档位列表中获取
 */
export const ContractPositionLevelList = selectorFamily({
  key: "ct-position-level-list",
  get:
    (symbolId: string) =>
    ({ get }) => {
      const loadable = get(
        noWait(CurrentBusinessLineContractPositionLevelListState)
      );
      if (loadable.state !== "hasValue") {
        return [];
      }
      const levelInfo = loadable.contents?.find(
        (config) => config.symbolId === symbolId
      );

      return levelInfo?.levels || [];
    },
});

/**
 * 获取传入的symbolId的仓位档位，从全局的仓位档位列表中获取
 */
export const GlobalContractPositionLevelList = selectorFamily({
  key: "global-ct-position-level-list",
  get:
    (symbolId: string) =>
    ({ get }): PositionLeverageLevel[] => {
      const loadable = get(noWait(GlobalContractPositionLevelListState));
      if (loadable.state !== "hasValue") {
        return [];
      }
      const levelInfo = loadable.contents?.find(
        (config) => config.symbolId === symbolId
      );

      return levelInfo?.levels || [];
    },
});

/**
 * 按照合约symbolId查询对应的配置信息(包含仓位档位信息)-当前业务线
 */
export const getMixContractInfoWithLevelBySymbolId = selectorFamily<
  MixContractInfo & { contractLevelList: PositionLeverageLevel[] },
  string
>({
  key: "get-mix-ct-info-with-level-selector",
  get:
    (symbolId: string) =>
    ({ get }) => {
      const info = get(getMixContractInfoBySymbolId(symbolId));
      const contractLevelList = get(ContractPositionLevelList(symbolId));
      return {
        ...info,
        contractLevelList,
      };
    },
});

export function getLevel(levels: PositionLeverageLevel[], hold: number) {
  return (
    levels.find((level) => {
      return level.startUnit <= hold && level.endUnit >= hold;
    }) || createDefaultPositionLevel()
  );
}

/**
 * 根据杠杆查询当前的最大可开
 * @param levels
 * @param hold
 * @returns
 */
export function getLevelByLeverage(
  levels: PositionLeverageLevel[],
  leverage: number
) {
  //防止档位为空的情况
  if (!levels.length) return createDefaultPositionLevel();
  let inverseLevels = levels.slice().reverse();
  let maxIndex = 0;
  for (let ilevel = 0; ilevel < inverseLevels.length; ++ilevel) {
    if (leverage > inverseLevels[ilevel].leverage) {
      maxIndex = ilevel;
    }
  }
  if (maxIndex === 0) return inverseLevels[maxIndex];
  let info =
    inverseLevels[maxIndex + 1] || inverseLevels[inverseLevels.length - 1];
  return info || createDefaultPositionLevel();
}

/**
 * 仅获取合约列表Map-当前业务线
 */
export const MixContractListMapSelector = selector<Map<string, string>>({
  key: "mix-ct-list-map-selector",
  get: ({ get }) => {
    const list = get(CurrentBusinessLineContractListState);
    return list.reduce((map, info) => {
      map.set(info.symbolId, info.symbolDisplayName);
      return map;
    }, new Map());
  },
});

/****
 * 获取 支持跟单的交易对列表
 */

export const MixContractSupportCopyListState = selector<
  Array<{ symbolId: string; symbolName: string }>
>({
  key: "mix-ct-support-copy",
  get: async () => {
    const data = await getOpenSymbol();
    if (isSuccess(data?.code)) {
      return data.data;
    } else {
      return [];
    }
  },
});

/**
 * 当前合约杠杆-逐仓-区分多空
 */
export const CurrentContractLeverageMapState = atom({
  key: "current-ct-leverage-map-state",
  default: {
    LONG: 1,
    SHORT: 1,
  },
});

/**
 * 修改触发 仓位 tab 切换 ，触发就切换，根据 currentTab 切换到 对应的
 */
export const ChangePositionTabChangeState = atom({
  key: "change-position-tab-change-state",
  default: {
    currentTab: 1,
    subCurtab: null,
    changeSigal: false,
  },
});

export const ContractProductCodeForFilterState = atom({
  key: "ct-product-code-for-filter-state",
  default: KeyOfOptionAll,
});

export const ContractMarginFilterState = atom({
  key: "ct-margin-for-filter-state",
  default: KeyOfOptionAll,
});

export const ContractPreferenceDrawerFlagState = atom({
  key: "ct-preference-drawer-flag-state",
  default: false,
});
// 合约设置弹出层
export const ContractCouponOpenPreferenceFlagState = atom({
  key: "ct-coupon-open-preference-flag-state",
  default: false,
});
// 合约设置弹出层-单双向自动展开开关
export const ContractPreferencePositionModeSetState = atom({
  key: "ct-preference-position-mode-state",
  default: false,
});
// 卡券中心开关
export const ContractOpenCouponDrawerState = atom({
  key: "ct-open-coupon-drawer-state",
  default: false,
});
// 卡券中心全局 loading
export const ContractCouponLoadingVisibleState = atom({
  key: "ct-coupon-loading-visible-state",
  default: false,
});

export const ContractPositionTicketCountState = atom({
  key: "ct-position-ticker-count-state",
  default: 0,
});

export const ContractMenuDrawerVisibleState = atom({
  key: "ct-menu-drawer-visible-state",
  default: false,
});

/**
 * 创世计划强制止盈止损 tips
 */
export const ContractCreationTips = atom<{
  symbol: string;
  side: string;
  ratio: string;
  tpslType: string | number;
}>({
  key: "ContractCreationTips",
  default: {
    symbol: "",
    side: "",
    ratio: "",
    tpslType: "",
  },
});
export const ContractCreationTipsVisible = atom<boolean>({
  key: "ContractCreationTipsVisible",
  default: false,
});

// 联合保证金业务线开关
export const ContractUnionModeSupportBusLines = atom<number[]>({
  key: "ContractUnionModeSupportBusLines",
  default: [],
});

export const ContractUnionModeVisibleSelector = selector<boolean>({
  key: "ContractUnionModeVisibleSelector",
  get: ({ get }) => {
    const businessLine = get(CurrentBusinessLineState);
    const supportBusinessLine = get(ContractUnionModeSupportBusLines);
    return supportBusinessLine.includes(businessLine);
  },
});

/**
 * 资产保证金模式：单币种保证金、联合保证金模式
 */
export const ContractUserAssetModeState = atom<USDT_ASSET_MODE_TYPE>({
  key: "ContractUserAssetModeState",
  default: USDT_ASSET_MODE_TYPE.SINGLE_MODE,
});

export const ContractIsUnionModeSelector = selector<boolean>({
  key: "ContractUnionModeSelector",
  get: ({ get }) => {
    const visible = get(ContractUnionModeVisibleSelector);
    const mode = get(ContractUserAssetModeState);
    return visible && mode === USDT_ASSET_MODE_TYPE.UNION_MODE;
  },
});
